<template>
  <div :class="`formulate-input-element formulate-input-element--${context.type}`" :data-type="context.type">
    <multiselect
      v-bind="context.attributes"
      label="label"
      :searchable="context.attributes.searchable"
      :multiple="context.attributes.multiple"
      :showLabels="context.attributes.showLabels"
      :trackBy="_trackBy"
      :options="_options"
      :value="_value"
      @input="returnValue"
      @keydown.tab.native="handleTab"
      :placeholder="context.attributes.placeholder"
      data-lpignore="true"
      ref="smartSelect"
    >
      <template slot="noResult">
        <!-- @slot Slot displaying message when no results are available. -->
        <slot name="noResult">No matches found.</slot>
      </template>

      <template #placeholder>
        <div class="flex ml-1 text-gray-400">
          {{ context.attributes.placeholder }}
        </div>
      </template>

      <template slot="singleLabel" slot-scope="props">
        <!-- @slot Slot used to override what is displayed as the selected option. -->
        <slot name="singleLabel" :option="props.option" />
      </template>

      <template slot="option" slot-scope="props">
        <!-- @slot Slot used to override what is displayed for each option. -->
        <slot name="option" :option="props.option" />
      </template>
    </multiselect>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'

export default {
  name: 'FormSmartSelect',
  inheritAttrs: false,
  components: {
    Multiselect
  },
  props: {
    context: {
      type: Object,
      required: true
    },
    /**
     * Label from option Object, that will be visible in the dropdown.
     */
    label: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      initialValue: null
    }
  },
  computed: {
    _value() {
      let val
      if (typeof this.context.model != 'object') {
        val = this._options.find((item) => item[this._trackBy] == this.context.model)
      } else {
        if (Array.isArray(this.context.model) && typeof this.context.model[0] != 'object') {
          // Must be string values so convert to objects.
          val = this.context.model.map((item) => {
            return this._options.find((o) => o[this._trackBy] == item) ?? { value: item, label: item }
          })
        } else {
          val = this.context.model
        }
      }

      return val
    },
    _options() {
      let options = this.context.attributes.smartOptions

      if (!Array.isArray(options) && typeof options == 'object') {
        let _options = []

        Object.keys(options).forEach((item) => {
          _options.push({ value: item, label: options[item] })
        })

        return _options
      } else if (typeof options[0] == 'string' || typeof options[0] == 'number') {
        return options.map((item) => {
          return { value: item, label: item }
        })
      }

      return options
    },
    _trackBy() {
      return this.context.attributes.trackBy ?? 'value'
    }
  },
  created() {
    this.initialValue = this.context.model ?? ''
  },
  mounted() {
    let input = this.$el?.querySelector('input') ?? {}
    input.autocomplete = 'off'
  },
  beforeDestroy() {
    this.$refs.smartSelect.deactivate()
  },
  methods: {
    updateValue(value) {
      if (typeof this.initialValue != 'object') {
        let val = value ? value[this._trackBy] : ''
        this.context.model = val
      } else {
        this.context.model = value
      }

      this.context.blurHandler()
    },
    /**
     * If the inital value is an array of non-objects (string/number)
     * then return array of simple values.
     */
    returnValue(value) {
      // if (Array.isArray(this.initialValue) && typeof this.initialValue[0] != 'object') {
      if (Array.isArray(value)) {
        let val = value.map((item) => {
          return item.value
        })
        this.context.model = val
      } else if (typeof value == 'object' && false) {
        this.context.model = value
      } else {
        let val = value ? value[this._trackBy] : ''
        this.context.model = val.toString()
      }

      this.context.blurHandler()

      // emit the whole object in case someone wants it
      this.$emit('object', value)
    },

    /**
     * This is a hack until vue-multiselect v3.0 is out of beta.
     * See https://github.com/shentao/vue-multiselect/pull/654
     */
    handleTab(event) {
      if (this.context.attributes.selectOnTab) this.$refs.smartSelect.addPointerElement()
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="postcss" scoped>
.multiselect >>> .multiselect__option--selected {
  @apply bg-blue-100;
}
.multiselect >>> .multiselect__option--highlight.multiselect__option--selected {
  @apply bg-red-200 text-red-800;
}
.multiselect >>> .multiselect__tag {
  @apply bg-blue-500 mb-0;
  margin-top: -3px;
  padding-top: 6px;
  padding-bottom: 6px;
}
.multiselect >>> .multiselect__tags {
  @apply cursor-pointer text-base;
  padding-top: 8px;
  line-height: 1rem;
}
.multiselect >>> .multiselect__single,
.multiselect >>> .multiselect__option {
  @apply font-normal;
}
.multiselect >>> .multiselect__single {
  padding-top: 3px;
  line-height: 1rem;
}
.multiselect >>> .multiselect__select {
  top: 8px;
  right: 5px;
  height: 24px;
  width: 24px;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23a0aec0'%3e%3cpath d='M15.3 9.3a1 1 0 0 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 1.4-1.4l3.3 3.29 3.3-3.3z'/%3e%3c/svg%3e");
}
.multiselect >>> .multiselect__select::before {
  border-width: 0;
}
.multiselect >>> .multiselect__tag-icon {
  line-height: 24px;
}
.multiselect >>> span.multiselect__placeholder,
.multiselect >>> input.multiselect__input::placeholder {
  @apply text-gray-500 font-normal opacity-100;
}
</style>

<style>
body .v--modal-overlay .v--modal-box,
.v--modal-overlay .v--modal-box {
  overflow: visible;
}
</style>
